import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [langDropdown, setLangDropdown] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' }
  ];

  const serviceLinks = [
    { name: 'Website Development', path: '/services/web-development' },
    { name: 'Mobile Apps', path: '/services/mobile-apps' },
    { name: 'Digital Marketing', path: '/services/digital-marketing' },
    { name: 'Travel Portals (B2B/B2C)', path: '/services/travel-portals' },
    { name: 'POS Systems', path: '/services/pos-systems' },
    { name: 'Booking Systems', path: '/services/booking-systems' },
    { name: 'Rental Systems', path: '/services/rental-systems' },
    { name: 'Real Estate Solutions', path: '/services/real-estate' },
    { name: 'Company Management', path: '/services/company-management' },
    { name: 'Pattaya Business Solutions', path: '/services/pattaya-solutions' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangDropdown(false);
  };

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isHomePage 
        ? (isScrolled ? 'bg-primary shadow-lg' : 'bg-transparent')
        : 'bg-primary shadow-lg'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0 group">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <span className="text-primary font-black text-lg sm:text-xl tracking-tight">KDS</span>
            </div>
            <div className="text-white">
              <div className="font-bold text-base sm:text-lg leading-tight">Karni Digital</div>
              <div className="text-xs sm:text-sm text-gold font-medium">Solutions</div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <div className="flex space-x-4 lg:space-x-6 items-center">
              <Link
                to="/"
                className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md relative group ${
                  location.pathname === '/' ? 'text-gold' : ''
                }`}
              >
                {t('nav.home')}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname === '/' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
              
              <Link
                to="/about"
                className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md relative group ${
                  location.pathname === '/about' ? 'text-gold' : ''
                }`}
              >
                {t('nav.about')}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname === '/about' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setServicesDropdown(true)}
                onMouseLeave={() => setServicesDropdown(false)}
              >
                <button
                  className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md flex items-center gap-1 ${
                    location.pathname.startsWith('/services') ? 'text-gold' : ''
                  }`}
                >
                  {t('nav.services')} 
                  <svg className={`w-4 h-4 transition-transform duration-300 ${
                    servicesDropdown ? 'rotate-180' : ''
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname.startsWith('/services') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
                
                {servicesDropdown && (
                  <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-xl overflow-hidden z-50">
                    {serviceLinks.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="block px-4 py-3 text-gray-800 hover:bg-primary hover:text-white transition-colors border-b border-gray-100 last:border-0"
                        onClick={() => setServicesDropdown(false)}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              <Link
                to="/portfolio"
                className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md relative group ${
                  location.pathname === '/portfolio' ? 'text-gold' : ''
                }`}
              >
                {t('nav.portfolio')}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname === '/portfolio' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
              
              <Link
                to="/pricing"
                className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md relative group ${
                  location.pathname === '/pricing' ? 'text-gold' : ''
                }`}
              >
                {t('nav.pricing')}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname === '/pricing' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
              
              <Link
                to="/blog"
                className={`text-white hover:text-gold transition-all duration-300 font-medium px-2 py-1 rounded-md relative group ${
                  location.pathname.startsWith('/blog') ? 'text-gold' : ''
                }`}
              >
                Blog
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${
                  location.pathname.startsWith('/blog') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
              
              <Link
                to="/contact"
                className={`bg-gold hover:bg-yellow-500 text-primary px-4 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105`}
              >
                {t('nav.contact')}
              </Link>
            </div>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangDropdown(!langDropdown)}
                className="flex items-center space-x-2 text-white hover:text-gold transition-colors"
              >
                <Globe size={20} />
                <span>{currentLang.flag}</span>
                <span className="text-sm">{currentLang.name}</span>
              </button>
              
              {langDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center space-x-3 ${
                        i18n.language === lang.code ? 'bg-primary-50' : ''
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="text-gray-800">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-primary border-t border-gold max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 pt-2 pb-6 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-all font-medium ${
                location.pathname === '/' ? 'text-gold bg-white/10' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              {t('nav.home')}
            </Link>
            
            <Link
              to="/about"
              className={`block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-all font-medium ${
                location.pathname === '/about' ? 'text-gold bg-white/10' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              {t('nav.about')}
            </Link>
            
            {/* Mobile Services Menu */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full px-4 py-3 text-white font-semibold flex items-center justify-between hover:bg-white/10 rounded-lg transition-all"
              >
                {t('nav.services')}
                <svg className={`w-4 h-4 transition-transform duration-300 ${
                  mobileServicesOpen ? 'rotate-180' : ''
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 space-y-1 bg-white/5 py-2 rounded-lg mt-1">
                  {serviceLinks.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-4 py-2 text-sm text-gray-200 hover:text-gold hover:bg-white/10 rounded transition-all"
                      onClick={() => {
                        setIsOpen(false);
                        setMobileServicesOpen(false);
                      }}
                    >
                      • {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to="/portfolio"
              className={`block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-all font-medium ${
                location.pathname === '/portfolio' ? 'text-gold bg-white/10' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              {t('nav.portfolio')}
            </Link>
            
            <Link
              to="/pricing"
              className={`block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-all font-medium ${
                location.pathname === '/pricing' ? 'text-gold bg-white/10' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              {t('nav.pricing')}
            </Link>
            
            <Link
              to="/blog"
              className={`block px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-all font-medium ${
                location.pathname.startsWith('/blog') ? 'text-gold bg-white/10' : ''
              }`}
              onClick={() => setIsOpen(false)}
            >
              Blog
            </Link>
            
            <Link
              to="/contact"
              className="block px-4 py-3 bg-gold text-primary hover:bg-yellow-500 rounded-lg transition-all font-semibold text-center shadow-md"
              onClick={() => setIsOpen(false)}
            >
              {t('nav.contact')}
            </Link>
            
            {/* Mobile Language Options */}
            <div className="pt-4 border-t border-gold">
              <div className="grid grid-cols-2 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsOpen(false);
                    }}
                    className={`flex items-center space-x-2 px-3 py-2 rounded ${
                      i18n.language === lang.code 
                        ? 'bg-gold text-primary' 
                        : 'text-white hover:text-gold'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="text-sm">{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
